import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { AlertTriangle } from "lucide-react";
import { Button, EmptyState, FormSkeleton, Input, Label, PageSkeleton, Switch, toast } from "@bs/ui";
import { HeaderActions, SettingsCard, SettingsPageFrame, useUnsavedGuard } from "../../../components/settings/settings-page.tsx";
import { Field } from "@bs/ui";
import { SimpleSelect } from "@bs/ui";
import { orpc } from "../../../lib/orpc.ts";
import { errorMessage } from "../../../lib/errors.ts";
import { INDIAN_STATES } from "../../../lib/india.ts";
import { STORE_TIMEZONES } from "@bs/contracts";
import { defaultWeightUnit, unitsForSystem, type UnitSystem, type WeightUnit } from "@bs/contracts/units";

export const Route = createFileRoute("/_store/settings/store-details")({
  pendingComponent: () => <PageSkeleton />,
  component: StoreDetailsPage,
});

interface FormState {
  storeName: string;
  legalName: string;
  supportEmail: string;
  supportPhone: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  pincode: string;
  timezone: string;
  unitSystem: UnitSystem;
  weightUnit: WeightUnit;
  autoPublishReviews: boolean;
}

interface SettingsData {
  storeName: string;
  legalName?: string | null | undefined;
  supportEmail?: string | null | undefined;
  supportPhone?: string | null | undefined;
  address?:
    | {
        countryCode?: string | undefined;
        line1?: string | undefined;
        line2?: string | undefined;
        city?: string | undefined;
        state?: string | undefined;
        pincode?: string | undefined;
      }
    | null
    | undefined;
  autoPublishReviews?: boolean | undefined;
  currency: string;
  timezone: string;
  country?: string | undefined;
  unitSystem?: UnitSystem | undefined;
  weightUnit?: WeightUnit | undefined;
  tax?: { gstin?: string | null | undefined } | undefined;
}
function toFormState(s: SettingsData): FormState {
  const unitSystem = s.unitSystem ?? "metric";
  const weightUnit = s.weightUnit ?? defaultWeightUnit(unitSystem);
  return {
    storeName: s.storeName,
    legalName: s.legalName ?? "",
    supportEmail: s.supportEmail ?? "",
    supportPhone: s.supportPhone ?? "",
    line1: s.address?.line1 ?? "",
    line2: s.address?.line2 ?? "",
    city: s.address?.city ?? "",
    state: s.address?.state ?? "",
    pincode: s.address?.pincode ?? "",
    timezone: s.timezone,
    unitSystem,
    weightUnit,
    autoPublishReviews: Boolean(s.autoPublishReviews),
  };
}

const TITLE = "Store details";
const DESCRIPTION = "Your store name, contact details and address. These appear on invoices and emails.";

export function StoreDetailsPage() {
  const query = useQuery(orpc.admin.settings.get.queryOptions());

  if (query.isLoading) {
    return (
      <SettingsPageFrame title={TITLE} description={DESCRIPTION}>
        <SettingsCard>
          <FormSkeleton />
        </SettingsCard>
      </SettingsPageFrame>
    );
  }
  if (query.isError || !query.data) {
    return (
      <SettingsPageFrame title={TITLE} description={DESCRIPTION}>
        <SettingsCard>
          <EmptyState
            icon={AlertTriangle}
            title="Could not load settings"
            description={errorMessage(query.error)}
            action={<Button onClick={() => void query.refetch()}>Try again</Button>}
          />
        </SettingsCard>
      </SettingsPageFrame>
    );
  }
  return <StoreDetailsForm data={query.data} />;
}

function StoreDetailsForm({ data }: { data: SettingsData }) {
  const queryClient = useQueryClient();
  const update = useMutation(orpc.admin.settings.update.mutationOptions());
  const baseline = toFormState(data);
  const [form, setForm] = useState<FormState>(baseline);
  const set = (k: keyof FormState) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);
  const guard = useUnsavedGuard(dirty);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const address = {
      countryCode: data.address?.countryCode ?? data.country ?? "IN",
      ...(form.line1 ? { line1: form.line1 } : {}),
      ...(form.line2 ? { line2: form.line2 } : {}),
      ...(form.city ? { city: form.city } : {}),
      ...(form.state ? { state: form.state } : {}),
      ...(form.pincode ? { pincode: form.pincode } : {}),
    };
    update.mutate(
      {
        storeName: form.storeName.trim(),
        legalName: form.legalName.trim() || null,
        supportEmail: form.supportEmail.trim() || null,
        supportPhone: form.supportPhone.trim() || null,
        address: Object.keys(address).length > 1 || data.address ? address : null,
        timezone: form.timezone,
        unitSystem: form.unitSystem,
        weightUnit: form.weightUnit,
        autoPublishReviews: form.autoPublishReviews,
      },
      {
        onSuccess: () => {
          toast.success("Store settings saved");
          void queryClient.invalidateQueries({ queryKey: orpc.admin.settings.key() });
        },
        onError: (err) => toast.error(errorMessage(err)),
      },
    );
  }

  return (
    <SettingsPageFrame title={TITLE} description={DESCRIPTION}>
      {guard}
      <HeaderActions>
        <Button type="submit" form="settings-store-details" disabled={update.isPending || !dirty || !form.storeName.trim()}>
          {update.isPending ? "Saving…" : "Save changes"}
        </Button>
      </HeaderActions>

      <form id="settings-store-details" onSubmit={onSubmit} className="flex flex-col gap-4">
        <SettingsCard title="Business details">
          <Field id="storeName" label="Store name">
            <Input id="storeName" required maxLength={120} value={form.storeName} onChange={set("storeName")} />
          </Field>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="legalName" className="text-xs font-medium text-foreground">Legal business name</Label>
              <span className="text-[11px] text-muted-foreground">Printed on tax invoices.</span>
            </div>
            <Input id="legalName" maxLength={200} value={form.legalName} onChange={set("legalName")} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field id="supportEmail" label="Support email">
              <Input id="supportEmail" type="email" value={form.supportEmail} onChange={set("supportEmail")} />
            </Field>
            <Field id="supportPhone" label="Support phone">
              <Input id="supportPhone" type="tel" value={form.supportPhone} onChange={set("supportPhone")} />
            </Field>
          </div>
        </SettingsCard>

        <SettingsCard title="Business address" description="Your registered or dispatch address.">
          <Field id="line1" label="Address line 1">
            <Input id="line1" value={form.line1} onChange={set("line1")} />
          </Field>
          <Field id="line2" label="Address line 2">
            <Input id="line2" value={form.line2} onChange={set("line2")} />
          </Field>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field id="city" label="City">
              <Input id="city" value={form.city} onChange={set("city")} />
            </Field>
            <Field id="state" label="State">
              <SimpleSelect id="state" value={form.state} placeholder="Select state" onChange={(v) => setForm((f) => ({ ...f, state: v }))} options={INDIAN_STATES.map((s) => ({ value: s, label: s }))} />
            </Field>
            <Field id="pincode" label="PIN code">
              <Input id="pincode" inputMode="numeric" maxLength={6} value={form.pincode} onChange={set("pincode")} />
            </Field>
          </div>
        </SettingsCard>

        <SettingsCard title="Regional defaults">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="currency" className="text-xs font-medium text-foreground">Currency</Label>
              <Input id="currency" disabled readOnly value={data.currency} />
              <span className="text-[11px] text-muted-foreground">Cannot be changed after orders exist.</span>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="timezone" className="text-xs font-medium text-foreground">Time zone</Label>
              <SimpleSelect
                id="timezone"
                value={form.timezone}
                placeholder="Select time zone"
                onChange={(v) => setForm((f) => ({ ...f, timezone: v }))}
                options={STORE_TIMEZONES.map((tz) => ({ value: tz, label: tz.replace(/_/g, " ") }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="country" className="text-xs font-medium text-foreground">Country</Label>
              <Input
                id="country"
                disabled
                readOnly
                value={data.country === "US" ? "United States (US)" : data.country === "CA" ? "Canada (CA)" : data.country === "IN" ? "India (IN)" : (data.country ?? "India")}
              />
              <span className="text-[11px] text-muted-foreground">Base market registration country.</span>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 pt-2 border-t border-border">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="unitSystem" className="text-xs font-medium text-foreground">Unit system</Label>
              <SimpleSelect
                id="unitSystem"
                value={form.unitSystem}
                onChange={(v) => {
                  const sys = v as UnitSystem;
                  setForm((f) => ({
                    ...f,
                    unitSystem: sys,
                    weightUnit: defaultWeightUnit(sys),
                  }));
                }}
                options={[
                  { value: "metric", label: "Metric (g, kg)" },
                  { value: "imperial", label: "Imperial (oz, lb)" },
                ]}
              />
              <span className="text-[11px] text-muted-foreground">Default unit system for entering and displaying weight.</span>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="weightUnit" className="text-xs font-medium text-foreground">Default weight unit</Label>
              <SimpleSelect
                id="weightUnit"
                value={form.weightUnit}
                onChange={(v) => setForm((f) => ({ ...f, weightUnit: v as WeightUnit }))}
                options={unitsForSystem(form.unitSystem).map((u) => ({
                  value: u,
                  label: u === "kg" ? "Kilograms (kg)" : u === "g" ? "Grams (g)" : u === "lb" ? "Pounds (lb)" : "Ounces (oz)",
                }))}
              />
              <span className="text-[11px] text-muted-foreground">Preferred unit in product variant and shipping editors.</span>
            </div>
          </div>
        </SettingsCard>

        <SettingsCard title="Tax identity" description={<span>Managed in <Link to="/settings/taxes" className="underline underline-offset-2 hover:text-foreground">Settings &gt; Taxes</Link></span>}>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="gstin" className="text-xs font-medium text-foreground">GSTIN</Label>
            <Input id="gstin" disabled readOnly value={data.tax?.gstin ?? "Not set"} />
          </div>
        </SettingsCard>

        <SettingsCard
          title="Review moderation"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <span className="text-sm font-medium text-foreground">Publish reviews automatically</span>
              <p className="text-xs text-muted-foreground">
                Reviews from verified purchases (delivered orders) are published immediately without manual approval.
              </p>
            </div>
            <Switch
              id="autoPublishReviews"
              checked={form.autoPublishReviews}
              onCheckedChange={(checked) => setForm((f) => ({ ...f, autoPublishReviews: checked }))}
            />
          </div>
        </SettingsCard>
      </form>
    </SettingsPageFrame>
  );
}
